import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { closeInfoPopup, closeConnectionPopup, setUserInterfaceDisabled } from "../../../../State/actions";
import { selectInfoPopupActive, selectInfoPopupKey, selectInfoPopupData, selectConnectionPopupActive } from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { getUITexture } from "../../../../assets";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Development mode
const DEV_MODE = false;

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.4;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Connection popup timeout settings (used by ReelControlContext)
const CONNECTION_POPUP_TIMEOUTS = {
  NATURAL_SPIN_DELAY: 350, // Buffer delay for regular spins (ms)
  MANUAL_STOP_BUFFER: 400, // Buffer delay for manual stops (ms)
};

// Info popup configuration
const INFO_POPUP_CONFIG = {
  basePadding: 40,
  baseMessageFontSize: 24,
  baseNumberFontSize: 32,
  baseContentWidth: 480,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.55 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.6 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.75 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.0 },
    },
  },
};

const InfoPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, device, orientation } = useDimensions();

  const infoPopupActive = useSelector(selectInfoPopupActive);
  const connectionPopupActive = useSelector(selectConnectionPopupActive);
  const messageKey = useSelector(selectInfoPopupKey);
  const infoPopupData = useSelector(selectInfoPopupData);

  // Connection popup is a special case — it shows connection_message and can't be tapped to close
  const isConnectionMode = connectionPopupActive && !infoPopupActive;
  const isOpen = DEV_MODE ? true : (infoPopupActive || connectionPopupActive);

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const popupContentRef = useRef(null);

  const deviceConfig = useMemo(() => {
    return INFO_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           INFO_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation]);

  const { textBefore, number, textAfter } = useMemo(() => {
    // Connection mode: use connection_message translation directly
    const effectiveKey = isConnectionMode ? "connection_message" : messageKey;
    let messageText = "";
    if (effectiveKey) {
      messageText = t(effectiveKey);
      if (messageText && infoPopupData && !isConnectionMode) {
        Object.keys(infoPopupData).forEach(key => {
          const placeholder = `{${key}}`;
          if (messageText.includes(placeholder)) {
            messageText = messageText.replace(new RegExp(`\\{${key}\\}`, 'g'), infoPopupData[key]);
          }
        });
      }
    }
    const numberMatch = messageText.match(/\S+\s+([\d.,]+(?:\s+[A-Z]{2,})*)/);
    const cleanedMatch = numberMatch ? numberMatch[1].trim() : null;
    if (cleanedMatch) {
      const numberIndex = messageText.indexOf(cleanedMatch);
      return {
        textBefore: messageText.substring(0, numberIndex).trim(),
        number: cleanedMatch,
        textAfter: messageText.substring(numberIndex + cleanedMatch.length).trim(),
      };
    }
    return { textBefore: messageText, number: null, textAfter: "" };
  }, [messageKey, infoPopupData, t, isConnectionMode]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Textures
  const frameTexture = useMemo(() => getUITexture("popup_frame_horizontal"), []);

  // Effective popup scale — clamped so the frame never exceeds 90% of viewport in either dimension.
  const popupScale = useMemo(() => {
    const deviceScale = deviceConfig.scaleMultiplier || 1.0;
    if (!frameTexture) return deviceScale;

    const idealContentWidth = INFO_POPUP_CONFIG.baseContentWidth * deviceScale;
    const idealPadding = INFO_POPUP_CONFIG.basePadding * deviceScale;
    const idealPanelWidth = idealContentWidth + idealPadding * 2;
    const scaleX = idealPanelWidth / frameTexture.width;
    const idealFrameWidth = frameTexture.width * scaleX;
    const idealFrameHeight = frameTexture.height * scaleX;

    const maxWidth = dimensions.width * 0.8;
    const maxHeight = dimensions.height * 0.8;
    const widthRatio = idealFrameWidth > maxWidth ? maxWidth / idealFrameWidth : 1;
    const heightRatio = idealFrameHeight > maxHeight ? maxHeight / idealFrameHeight : 1;
    const clampRatio = Math.min(widthRatio, heightRatio);

    return deviceScale * clampRatio;
  }, [deviceConfig, frameTexture, dimensions.width, dimensions.height]);

  const padding = useMemo(() => INFO_POPUP_CONFIG.basePadding * popupScale, [popupScale]);
  const messageFontSize = useMemo(() => Math.round(INFO_POPUP_CONFIG.baseMessageFontSize * popupScale), [popupScale]);
  const numberFontSize = useMemo(() => Math.round(INFO_POPUP_CONFIG.baseNumberFontSize * popupScale), [popupScale]);
  const contentWidth = useMemo(() => INFO_POPUP_CONFIG.baseContentWidth * popupScale, [popupScale]);

  const lineGap = useMemo(() => messageFontSize * 0.3, [messageFontSize]);

  // White text, no outline
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: messageFontSize,
    fontWeight: "400",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: contentWidth,
    lineHeight: messageFontSize * 1.2,
    breakWords: false,
  }), [messageFontSize, contentWidth]);

  const numberStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: numberFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
  }), [numberFontSize]);

  const textBeforeUpper = useMemo(() => textBefore.toUpperCase(), [textBefore]);
  const numberUpper = useMemo(() => number ? number.toUpperCase() : null, [number]);
  const textAfterUpper = useMemo(() => textAfter.toUpperCase(), [textAfter]);

  const textBeforeMetrics = useMemo(() => TextMetrics.measureText(textBeforeUpper, messageStyle), [textBeforeUpper, messageStyle]);
  const numberMetrics = useMemo(() => numberUpper ? TextMetrics.measureText(numberUpper, numberStyle) : { height: 0 }, [numberUpper, numberStyle]);
  const textAfterMetrics = useMemo(() => TextMetrics.measureText(textAfterUpper, messageStyle), [textAfterUpper, messageStyle]);

  const totalContentHeight = useMemo(
    () => textBeforeMetrics.height + (number ? lineGap + numberMetrics.height + lineGap : 0) + textAfterMetrics.height,
    [textBeforeMetrics.height, number, lineGap, numberMetrics.height, textAfterMetrics.height]
  );

  // Frame scaling — uniform scale to fit content while keeping aspect ratio
  const desiredPanelWidth = useMemo(() => contentWidth + padding * 2, [contentWidth, padding]);
  const desiredPanelHeight = useMemo(() => totalContentHeight + padding * 2, [totalContentHeight, padding]);

  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    const scaleX = desiredPanelWidth / frameTexture.width;
    const scaleY = desiredPanelHeight / frameTexture.height;
    return Math.max(scaleX, scaleY);
  }, [frameTexture, desiredPanelWidth, desiredPanelHeight]);

  const frameSpriteWidth = useMemo(() => frameTexture ? frameTexture.width * frameScale : 0, [frameTexture, frameScale]);
  const frameSpriteHeight = useMemo(() => frameTexture ? frameTexture.height * frameScale : 0, [frameTexture, frameScale]);

  // Content positions relative to center (0,0 = center of popup)
  const { textBeforeY, numberY, textAfterY } = useMemo(() => {
    if (!number) {
      return { textBeforeY: 0, numberY: 0, textAfterY: 0 };
    } else {
      const contentStartY = -totalContentHeight / 2;
      return {
        textBeforeY: contentStartY + textBeforeMetrics.height / 2,
        numberY: contentStartY + textBeforeMetrics.height + lineGap + numberMetrics.height / 2,
        textAfterY: contentStartY + textBeforeMetrics.height + lineGap + numberMetrics.height + lineGap + textAfterMetrics.height / 2,
      };
    }
  }, [number, totalContentHeight, textBeforeMetrics.height, lineGap, numberMetrics.height, textAfterMetrics.height]);

  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsVisible(true);
      dispatch(setUserInterfaceDisabled(true));
      // 1. Overlay fades in first
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          // 2. Then popup fades in after overlay is done
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (!isOpen && !isClosing) {
      dispatch(setUserInterfaceDisabled(false));
      setIsVisible(false);
      setOverlayAlpha(0);
      setHasAnimated(false);
    }
  }, [isOpen, isClosing, dispatch]);

  useEffect(() => {
    if (!isClosing) return;
    let overlayAnim = null;
    let popupAnim = null;
    if (popupContentRef.current) {
      // 1. Popup fades out first
      popupAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
      anime({
        targets: popupContentRef.current.scale,
        x: 0.85,
        y: 0.85,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
    }
    // 2. Then overlay fades out after popup is done
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: POPUP_ANIM_DURATION,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeInfoPopup());
        dispatch(closeConnectionPopup());
        setIsClosing(false);
        setIsVisible(false);
        setHasAnimated(false);
      },
    });
    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch]);

  const handleClose = useCallback(() => {
    if (isClosing || isConnectionMode) return; // Connection popup can't be manually closed
    soundManager.play(SOUND_NAME.button_click);
    setIsClosing(true);
  }, [isClosing, isConnectionMode]);

  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Container name="info_popup" zIndex={1005} sortableChildren={true}>
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        cursor={isConnectionMode ? "default" : "pointer"}
        pointerdown={(e) => {
          e.stopPropagation();
          if (!isConnectionMode) handleClose();
        }}
        zIndex={1005}
      />

      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={1006}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
        eventMode={isConnectionMode ? "none" : "static"}
        cursor={isConnectionMode ? "default" : "pointer"}
        pointerdown={isConnectionMode ? undefined : (e) => {
          e.stopPropagation();
          handleClose();
        }}
        accessible={true}
        accessibleTitle={`${textBefore}${number ? ` ${number}` : ""}${textAfter ? ` ${textAfter}` : ""}`}
      >
        {/* Frame image background */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Text before number */}
        <Text
          text={textBeforeUpper}
          style={messageStyle}
          anchor={0.5}
          x={0}
          y={textBeforeY}
          resolution={2}
        />

        {/* Number (bold and larger) */}
        {numberUpper && (
          <Text
            text={numberUpper}
            style={numberStyle}
            anchor={0.5}
            x={0}
            y={numberY}
            resolution={2}
          />
        )}

        {/* Text after number */}
        {textAfterUpper && (
          <Text
            text={textAfterUpper}
            style={messageStyle}
            anchor={0.5}
            x={0}
            y={textAfterY}
            resolution={2}
          />
        )}
      </Container>
    </Container>
  );
};

export { CONNECTION_POPUP_TIMEOUTS };
export default InfoPopup;
